import { Component, Input, OnInit, ViewChild, ElementRef, ChangeDetectorRef, HostListener } from '@angular/core'
import { CommonModule, DatePipe, Location } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { ComboboxComponent, Option } from '@components/combobox/combobox.component'
import { ButtonComponent } from '@components/button/button.component'
import { ProgressStep } from 'app/interfaces/progress-step.interface'
import { ProgressBarComponent } from '@components/progress-bar/progress-bar.component'
import { DateFilterComponent } from '@components/date-filter/date-filter.component'
import { ApiService } from 'app/services/api.service'
import { CatalogueColorDTO } from 'app/interfaces/api/CatalogueColorDTO'
import { ActivatedRoute, Router } from '@angular/router'
import { DraftSaleRecord } from 'app/interfaces/draft-response.interface'
import { forkJoin } from 'rxjs'
import { HeaderComponent } from '@components/header/header.component'
import { AlertComponent } from '@components/alert/alert.component'
import { ConfirmModalComponent } from '@components/confirm-modal/confirm-modal.component'
import { CheckboxComponent } from '@components/checkbox/checkbox.component'
import { RadioComponent } from '@components/radio/radio.component'
import { SelectComponent } from '@components/select/select.component'
import { SelectValue } from '../../interfaces/select-value.interface'
import {
    MatDatepicker, MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepickerToggleIcon
} from '@angular/material/datepicker'
import { MatFormField, MatSuffix } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core'
import { AnalyticsService } from 'app/services/analytics.service'
import { DateTime } from 'luxon'
import { SalesService } from '../../services/sales.service'
import { FabricatorSelectInvoicesComponent } from './fabricator-select-invoices/fabricator-select-invoices.component'
import { InvoiceData } from '../../interfaces/invoice-claim.interface'
import { SelectPostalComponent } from '@components/select-postal/select-postal.component'
import { SaveSalesDraftBody, UpdateSaleBody } from '../../interfaces/sales-body.interface'
import { PlatformService, PlatformName } from 'app/services/platform.service'

const WARRANTY_LINKS_BY_LANG: Record<string, string> = {
    cs: 'warranty-cs-cz',
    da: 'warranty-da-dk',
    de: 'warranty-de-de',
    'en-au': 'warranty-en',
    'en-gb': 'warranty-en',
    en: 'warranty-en',
    es: 'garantia-es',
    fi: 'warranty-fi-fi',
    fr: 'warranty-fr-fr',
    'fr-ca': 'warranty-fr-fr',
    he: 'warranty-he-il',
    iw: 'warranty-he-il',
    hu: 'warranty-hu-hu',
    it: 'warranty-it-it',
    nl: 'warranty-nl-nl',
    no: 'warranty-no-no',
    pl: 'warranty-pl-pl',
    pt: 'warranty-pt-pt',
    sv: 'warranty-sv-se',
    tr: 'warranty-tr-tr'
}

@Component({
    selector: 'app-register-sales-form',
    standalone: true,
    imports: [
        CommonModule, FormsModule, TranslateModule, ButtonComponent, ProgressBarComponent,
        HeaderComponent, AlertComponent, ConfirmModalComponent, CheckboxComponent,
        RadioComponent, SelectComponent, MatFormField, MatInput, MatDatepickerToggle, MatIcon, MatDatepicker, MatDatepickerInput,
        MatSuffix,
        MatDatepickerToggleIcon, FabricatorSelectInvoicesComponent, SelectPostalComponent
    ],
    providers: [provideNativeDateAdapter()],
    templateUrl: './register-sales-form.component.html',
    styleUrls: ['./register-sales-form.component.scss']
})
export class RegisterSalesFormComponent implements OnInit {
    @Input({ required: true }) creating: boolean

    isFabricatorForm: boolean;
    isDistributorForm: boolean;

    needToSelectShop: boolean
    shopList: SelectValue[] = []
    selectedShop: SelectValue | null = null
    canContinueToSalesCreation: boolean = false

    readonly ACTIOS_CUT_OFF_DAYS = SalesService.ACTIOS_CUT_OFF_DAYS

    public submitAttempted = false

    public showFinishModal = false

    @HostListener('document:keydown.escape', ['$event'])
    onEscKey() {
        if (this.showFinishModal) this.closeFinishModal()
    }

    private pendingTeamMemberId: string | null = null
    private pendingProfessionalId: string | null = null

    showSummary = false
    /*
      Step 1 -> Add/Edit Applications
      Step 2 -> Invoice Details
      Step 3 -> Warranty
     */
    public step = 1
    public addingNew = false
    public editingIndex: number | null = null
    public isDragging = false
    public uploadError: string | null = null
    public invoiceDateError = false
    public acceptTerms = false
    public termsAndConditionsLink: string;
    public warrantyConditionsLink: string;

    get warrantyFormLink(): string {
        const lang = (this.translate.currentLang || this.translate.getDefaultLang() || 'en').toLowerCase()
        const path = WARRANTY_LINKS_BY_LANG[lang] || WARRANTY_LINKS_BY_LANG[lang.split('-')[0]] || 'warranty-en'
        return `https://forms.cosentino.com/${path}/`
    }
    public isSelecting = false

    public brands: string[] = []
    public colors: CatalogueColorDTO[] = []
    public loadingCollaborators = true
    public teamMembers: any[] = []
    public loadingTeamMembers = true

    public devSkipToWarranty = false

    public todayDate: string = this.formatDateMMDDYYYY(new Date())

    public isConfirmActivateWarrantyModalOpen = false

    countries: SelectValue[] = []
    userInfo: any

    public selectedWarrantyOption: 'activate' | 'instructions' | null = null
    public warrantyFormMode: 'activate' | 'instructions' | null = null
    private forceWarrantyForm: boolean = false

    public addedApplications: {
        id?: string;
        application: string;
        brand: string;
        color: string;
        colorCode: string;
        units: string;
        sku?: string;
    }[] = []

    public uploadedInvoices: {
        name: string;
        type: string;
        size: number;
        file?: File;
        url?: string;
        isRemote?: boolean;
    }[] = []

    public invoiceForm = {
        professional: '',
        teamMember: '',
        invoiceDate: ''
    }

    invoiceIdFab: string | null = null

    public form = {
        application: '',
        units: '',
        brand: '',
        color: '',
        colorCode: ''
    }

    applicationOptions: SelectValue[] = []
    public selectedApplication: SelectValue | null = null
    unitsOptions: SelectValue[] = []
    public selectedUnits: SelectValue | null = null
    brandOptions: SelectValue[] = []
    public selectedBrand: SelectValue | null = null
    colorOptions: SelectValue[] = []
    public selectedColor: SelectValue | null = null
    professionalOptions: SelectValue[] = []
    public selectedProfessional: SelectValue | null = null
    teamMemberOptions: SelectValue[] = []
    public selectedTeamMember: SelectValue | null = null

    public selectedCountry: SelectValue | null = null

    maxInvoiceDate = new Date()
    invoiceDateValue: Date | null = null
    invoiceDateString: string | null = null

    public warrantyForm: any = {
        name: '',
        surname: '',
        country: '',
        zip: '',
        state: '',
        city: '',
        address: '',
        email: '',
        phone: '',
        invoiceDate: '',
        gdprConfirmation: false,
        clientApproval: false,
        instructionOption: 'mail'
    }

    public progressSteps: ProgressStep[] = [
        { title: 'my_sales.progress_step_1' },
        { title: 'my_sales.progress_step_2' },
        { title: 'my_sales.progress_step_3' }
    ]
    private applicationsWithUnits = ['Sink', 'Washbasin', 'Shower Tray']
    private readonly MAX_FILE_SIZE = 8 * 1024 * 1024
    private readonly MIN_DPI = 96
    private readonly ALLOWED_FILE_TYPES = ['image/png', 'image/jpeg', 'application/pdf']

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>
    collaborators: SelectValue[]

    public saleId: string | null = null
    private kbStudio: string | null = null
    private status: string | null = null
    loadingBrands: boolean
    loadingColors: boolean

    isUserFromUsaCa: boolean
    isRetailUser: boolean

    salePoints: string | null = null

    get salesBannerLang(): 'fr' | 'en' {
        const lang = (this.translate.currentLang || this.translate.getDefaultLang() || 'en').toLowerCase()
        return lang === 'fr' || lang === 'fr-ca' ? 'fr' : 'en'
    }

    private get salesBannerCountryLang(): 'US_EN' | 'CA_EN' | 'CA_FR' {
        const country = this.api.userInfo.value?.country?.toUpperCase()
        if (country === 'CA' && this.salesBannerLang === 'fr') return 'CA_FR'
        if (country === 'CA') return 'CA_EN'
        return 'US_EN'
    }

    private static readonly SALES_BANNER_URLS: Record<'US_EN' | 'CA_EN' | 'CA_FR', { desktop: string; tablet: string; pdf: string }> = {
        US_EN: {
            desktop: 'https://static.cosentino.com/ImagesCommunityCosentinoWe/homeBanners/SHOP-SAVE/US/shop-save-2026-EN-1440x252.jpg',
            tablet:  'https://static.cosentino.com/ImagesCommunityCosentinoWe/homeBanners/SHOP-SAVE/US/shop-save-2026-EN-656x170.jpg',
            pdf:     'https://static.cosentino.com/we.cosentino.com/2026/Promos/Shop%20%26%20Save%20Flyer%20v6%20USA%20tr%20DIGITAL.pdf'
        },
        CA_EN: {
            desktop: 'https://static.cosentino.com/ImagesCommunityCosentinoWe/homeBanners/SHOP-SAVE/US/shop-save-2026-%201440x252.jpg',
            tablet:  'https://static.cosentino.com/ImagesCommunityCosentinoWe/homeBanners/SHOP-SAVE/US/shop-save-2026-%20656x170.jpg',
            pdf:     'https://static.cosentino.com/we.cosentino.com/2026/Promos/Shop%20%26%20Save%202026%20Flyer%20CAN%20v6%20DIGITAL.pdf'
        },
        CA_FR: {
            desktop: 'https://static.cosentino.com/ImagesCommunityCosentinoWe/homeBanners/SHOP-SAVE/FR/shop-save-2026-%20FR-1440x252.jpg',
            tablet:  'https://static.cosentino.com/ImagesCommunityCosentinoWe/homeBanners/SHOP-SAVE/FR/shop-save-2026-%20FR-656x170.jpg',
            pdf:     'https://static.cosentino.com/we.cosentino.com/2026/Promos/Shop%20%26%20Save%202026%20Flyer%20CAN%20v6%20DIGITAL.pdf'
        }
    }

    get salesBannerDesktopUrl(): string {
        return RegisterSalesFormComponent.SALES_BANNER_URLS[this.salesBannerCountryLang].desktop
    }

    get salesBannerTabletUrl(): string {
        return RegisterSalesFormComponent.SALES_BANNER_URLS[this.salesBannerCountryLang].tablet
    }

    get salesBannerPdfUrl(): string {
        return RegisterSalesFormComponent.SALES_BANNER_URLS[this.salesBannerCountryLang].pdf
    }

    public currentPlatform: PlatformName

    constructor(
        private api: ApiService,
        private cdr: ChangeDetectorRef,
        private router: Router,
        private route: ActivatedRoute,
        private translate: TranslateService,
        private location: Location,
        private dateAdapter: DateAdapter<any>,
        private analytics: AnalyticsService,
        private platformService: PlatformService,
    ) {
        this.currentPlatform = this.platformService.getPlatform()
    }

    private translations(){
        this.translate.get([
            'my_sales.progress_step_1',
            'my_sales.progress_step_2',
            'my_sales.progress_step_3',
        ])
    }

    // ------------------ LIFECYCLE ------------------
    ngOnInit(): void {

        if (this.api.userInfo.value && this.api.userInfo.value.country) {
            this.userInfo = this.api.userInfo.value
            this.isUserFromUsaCa = this.api.userInfo.value.country.toUpperCase() === 'US' || this.api.userInfo.value.country.toUpperCase() === 'CA'

            this.translate.onLangChange.subscribe((value) => {
                this.updateMatLang(value.lang)
            })
            this.updateMatLang(this.translate.currentLang || undefined)

            this.isFabricatorForm = this.api.userInfo.value.accountType === 'FABRICATOR'
            this.isRetailUser = this.api.userInfo.value.accountType === 'RETAIL'
            this.isDistributorForm = this.api.userInfo.value.accountType === 'DISTRIBUTOR'

            this.isRetailUser = this.api.userInfo.value.accountType === 'RETAIL'

            if(this.isFabricatorForm){
                this.invoiceForm.professional = this.api.userInfo.value.salesforceAccountId || ''
                this.kbStudio = this.invoiceForm.professional
                this.invoiceForm.teamMember = this.api.userInfo.value.salesforceContactId || ''
            }

            if(this.isDistributorForm){
                this.invoiceForm.professional = this.api.userInfo.value.salesforceAccountId || ''
                this.kbStudio = this.invoiceForm.professional
                this.invoiceForm.teamMember = this.api.userInfo.value.salesforceContactId || ''
            }

            this.setCountries()

            //TODO This might be wrong for fabricators
            this.api.getContractInfo('retail-sales').subscribe(contract => {
                this.termsAndConditionsLink = contract.contractUrl
            })

            this.api.getContractInfo('warranty-agreement').subscribe(contract => {
                this.warrantyConditionsLink = contract.contractUrl
            })

        }

        this.translate.onLangChange.subscribe(() => {
            // refrescar labels dependientes de i18n
            this.cdr.detectChanges()
        })


        this.route.params.subscribe((params) => {
            const id = params['id']
            if (id) {
                // EDICIÓN → NO pongas invoiceDate por defecto
                this.saleId = id
                this.loadSalesAndPositionSteps(id)
            } else {
                // CREACIÓN → aquí sí inicializamos por defecto
                this.addingNew = this.addedApplications.length === 0
                this.invoiceForm.invoiceDate = this.getTodayDate()
                this.setupChildAccounts()
            }
        })

        forkJoin({
            brands: this.api.myGalleries__brands(),
            colors: this.api.mySales__catalogueItems()
        }).subscribe({
            next: ({ brands, colors }) => {
                this.applicationOptions = this.getApplicationOptions()
                this.unitsOptions = this.getUnitsOptions()
                this.brands = brands
                this.brandOptions = this.getBrandsOptions()
                this.colors = colors
                this.colorOptions = this.getColorOptions()
                this.loadingBrands = false
                this.loadingColors = false
                this.cdr.detectChanges()

                // Procesar el ID después de cargar datos
                this.route.params.subscribe((params) => {
                    const id = params['id']
                    const warrantyStep = params['warrantyStep']
                    if (id) {
                        this.saleId = id
                        this.loadSalesAndPositionSteps(id, warrantyStep)
                    }
                    if (warrantyStep) {
                        console.log('going to warranty step', warrantyStep)
                        this.step = 3
                        this.selectedWarrantyOption = warrantyStep
                        this.warrantyFormMode = warrantyStep
                        this.forceWarrantyForm = warrantyStep == 'activate'
                    }
                })
            },
            error: (err) => {
                console.error('Error al cargar datos iniciales:', err)
                this.loadingBrands = false
                this.loadingColors = false
                this.cdr.detectChanges()
            }
        })

        // 1) Cargas “normales”
        if (this.devSkipToWarranty) {
            this.step = 3
            this.selectedWarrantyOption = 'activate'
            this.warrantyFormMode = 'activate'
        } else {
            this.addingNew = this.addedApplications.length === 0
            this.invoiceForm.invoiceDate = this.getTodayDate()
        }

        this.api.myGalleries__brands().subscribe(brands => (this.brands = brands))
        this.api.mySales__catalogueItems().subscribe({
            next: (colors) => {
                this.colors = colors
                this.cdr.detectChanges()
            },
            error: (error) => {
                console.error('Error al obtener colores del catálogo:', error)
                this.colors = []
                this.cdr.detectChanges()
            }
        })

        this.loadCollaborators()
        this.loadTeamMembers()

        // 2) Si viene un :id en la url, cargar claim y posicionar step
        this.route.params.subscribe((params) => {
            const id = params['id']
            if (id) {
                this.saleId = id
                this.loadSalesAndPositionSteps(id)
            }
        })
    }

    private setCountries() {
        this.countries = [
            { label: this.translate.instant('countries.DE'), value: 'DE', id: 'DE' },
            { label: this.translate.instant('countries.AD'), value: 'AD', id: 'AD' },
            { label: this.translate.instant('countries.AE'), value: 'AE', id: 'AE' },
            { label: this.translate.instant('countries.AR'), value: 'AR', id: 'AR' },
            { label: this.translate.instant('countries.AT'), value: 'AT', id: 'AT' },
            { label: this.translate.instant('countries.AU'), value: 'AU', id: 'AU' },
            { label: this.translate.instant('countries.BE'), value: 'BE', id: 'BE' },
            { label: this.translate.instant('countries.BR'), value: 'BR', id: 'BR' },
            { label: this.translate.instant('countries.CA'), value: 'CA', id: 'CA' },
            { label: this.translate.instant('countries.CH'), value: 'CH', id: 'CH' },
            { label: this.translate.instant('countries.CL'), value: 'CL', id: 'CL' },
            { label: this.translate.instant('countries.CO'), value: 'CO', id: 'CO' },
            { label: this.translate.instant('countries.CZ'), value: 'CZ', id: 'CZ' },
            { label: this.translate.instant('countries.DK'), value: 'DK', id: 'DK' },
            { label: this.translate.instant('countries.EE'), value: 'EE', id: 'EE' },
            { label: this.translate.instant('countries.ES'), value: 'ES', id: 'ES' },
            { label: this.translate.instant('countries.FI'), value: 'FI', id: 'FI' },
            { label: this.translate.instant('countries.FR'), value: 'FR', id: 'FR' },
            { label: this.translate.instant('countries.GB'), value: 'GB', id: 'GB' },
            { label: this.translate.instant('countries.HU'), value: 'HU', id: 'HU' },
            { label: this.translate.instant('countries.ID'), value: 'ID', id: 'ID' },
            { label: this.translate.instant('countries.IE'), value: 'IE', id: 'IE' },
            { label: this.translate.instant('countries.IL'), value: 'IL', id: 'IL' },
            { label: this.translate.instant('countries.IS'), value: 'IS', id: 'IS' },
            { label: this.translate.instant('countries.IT'), value: 'IT', id: 'IT' },
            { label: this.translate.instant('countries.JP'), value: 'JP', id: 'JP' },
            { label: this.translate.instant('countries.KZ'), value: 'KZ', id: 'KZ' },
            { label: this.translate.instant('countries.LI'), value: 'LI', id: 'LI' },
            { label: this.translate.instant('countries.LT'), value: 'LT', id: 'LT' },
            { label: this.translate.instant('countries.LU'), value: 'LU', id: 'LU' },
            { label: this.translate.instant('countries.LV'), value: 'LV', id: 'LV' },
            { label: this.translate.instant('countries.MH'), value: 'MH', id: 'MH' },
            { label: this.translate.instant('countries.MX'), value: 'MX', id: 'MX' },
            { label: this.translate.instant('countries.MY'), value: 'MY', id: 'MY' },
            { label: this.translate.instant('countries.NL'), value: 'NL', id: 'NL' },
            { label: this.translate.instant('countries.NO'), value: 'NO', id: 'NO' },
            { label: this.translate.instant('countries.NZ'), value: 'NZ', id: 'NZ' },
            { label: this.translate.instant('countries.PL'), value: 'PL', id: 'PL' },
            { label: this.translate.instant('countries.PR'), value: 'PR', id: 'PR' },
            { label: this.translate.instant('countries.PT'), value: 'PT', id: 'PT' },
            { label: this.translate.instant('countries.SA'), value: 'SA', id: 'SA' },
            { label: this.translate.instant('countries.SE'), value: 'SE', id: 'SE' },
            { label: this.translate.instant('countries.SG'), value: 'SG', id: 'SG' },
            { label: this.translate.instant('countries.SI'), value: 'SI', id: 'SI' },
            { label: this.translate.instant('countries.SK'), value: 'SK', id: 'SK' },
            { label: this.translate.instant('countries.TR'), value: 'TR', id: 'TR' },
            { label: this.translate.instant('countries.US'), value: 'US', id: 'US' },
            { label: this.translate.instant('countries.ZA'), value: 'ZA', id: 'ZA' }
        ]

        this.countries.sort((a, b) => a.label.localeCompare(b.label))

        this.selectedCountry = this.countries.find(country => this.api.userInfo.value.country === country.id) || null
        this.warrantyForm.country = this.selectedCountry?.id || ''
    }

    private setupChildAccounts(){
        if (this.api.userInfo.value.hasChildAccounts) {
            this.api.getChildAccounts().subscribe({
                next: (accounts) => {
                    if(accounts === null || accounts.length <= 1){
                        this.needToSelectShop = false
                        this.canContinueToSalesCreation = true
                        return;
                    }
                    for(let account of accounts) {
                        const value = {
                            id: account.Id,
                            value: account.Id,
                            label: account.Name + ' - ' + account.BillingStreet + ' (' + account.BillingPostalCode + ') ' + account.BillingCity + ', ' + account.BillingState
                        }
                        this.shopList.push(value)
                        if(value.id === this.kbStudio){
                            this.selectedShop = value
                            this.needToSelectShop = false
                            return;
                        }
                    }
                    this.needToSelectShop = true
                },
                error: (err) => {
                    console.error('Error al cargar datos iniciales:', err)
                    this.loadingBrands = false
                    this.loadingColors = false
                    // this.needToSelectShop = false
                    // this.canContinueToSalesCreation = true
                }
            })
        } else {
            this.needToSelectShop = false
            this.canContinueToSalesCreation = true
        }
    }

    private loadCollaborators(){
        this.loadingCollaborators = true
        this.api.mySales__relatedAccounts().subscribe({
            next: (collaborators) => {
                this.collaborators = this.transformCollaborators(collaborators)
                this.loadingCollaborators = false
                this.professionalOptions = this.getProfessionalOptions()

                if (this.pendingProfessionalId && !this.invoiceForm.professional) {
                    const found = this.collaborators.find(c => c.value === this.pendingProfessionalId)
                    if (found) this.invoiceForm.professional = found.value
                    else this.invoiceForm.professional = this.pendingProfessionalId // fallback
                    this.pendingProfessionalId = null
                }

                this.cdr.detectChanges()
            },
            error: (error) => {
                console.error('Error al obtener colaboradores:', error)
                this.loadingCollaborators = false
                this.collaborators = [{ id: 'error', value: 'error', label: 'Error al cargar' }]
                this.cdr.detectChanges()
            }
        })
    }

    private loadTeamMembers(){
        this.loadingTeamMembers = true
        this.api.getTeamMembers(this.kbStudio).subscribe({
            next: (teamMembers) => {
                this.teamMembers = teamMembers
                this.loadingTeamMembers = false
                this.teamMemberOptions = this.getTeamMemberOptions()

                // Si venía un ContactId en el draft, lo aplicamos ahora que ya tenemos opciones
                if (this.pendingTeamMemberId && !this.invoiceForm.teamMember) {
                    const found = this.teamMemberOptions.find(tm => tm.id === this.pendingTeamMemberId)
                    if (found) {
                        // Tu combobox usa value de transformCollaborators -> prioriza id
                        this.invoiceForm.teamMember = (found.id || '').toString()
                        this.selectedTeamMember = found || null
                    }
                    this.pendingTeamMemberId = null
                }

                this.cdr.detectChanges()
            },
            error: (error) => {
                console.error('Error al obtener team members:', error)
                this.loadingTeamMembers = false
                this.teamMembers = []
                this.cdr.detectChanges()
            }
        })
    }

    private updateMatLang(lang?: string) {
        if (lang) {
            this.dateAdapter.setLocale(lang)
            return
        }

        if (this.api.userInfo.value.locale) {
            this.dateAdapter.setLocale(this.api.userInfo.value.locale.slice(0, 2))
        } else {
            this.dateAdapter.setLocale('en')
        }
    }

    // ------------------ ROUTE/CLAIM LOGIC ------------------
    private loadSalesAndPositionSteps(id: string, warrantyStep?: 'activate' | 'instructions' | null): void {
        this.api.getDraftSale(id).subscribe({
            next: (resp: { records: (DraftSaleRecord | undefined)[] }) => {
                const rec = resp?.records?.[0] as DraftSaleRecord | undefined
                if (!rec) return

                this.saleId = rec.Id
                this.kbStudio = rec.KB_Studio__c
                this.status = rec.Status__c
                this.invoiceIdFab = rec.Invoice_Number__c

                // ----- Step 1: items -----
                const jobItems = (rec.Job_Items__r as any)?.records || rec.Job_Items__r || []
                if (Array.isArray(jobItems) && jobItems.length > 0) {
                    this.addedApplications = jobItems.map((item: any) => ({
                        id: item?.Id || '',
                        application: item?.Application_Type__c || '',
                        brand: item?.Brand__c || item?.Product__r?.Brand__c || '',
                        color: item?.Product__r?.Name || '',
                        colorCode: item?.Product__r?.Color__c || '',
                        sku: item?.Product__r?.Matnr__c || '',
                        units: (item?.Quantity__c || '1').toString()
                    }))
                    this.addingNew = false
                } else {
                    this.addingNew = true
                }

                // ----- Step 2: invoice -----
                const urls: string[] = [
                    rec.URL_Invoice_attached__c,
                    rec.URL_Invoice_attached_2__c,
                    rec.URL_Invoice_attached_3__c
                ].filter(Boolean) as string[]

                // Fecha de factura (si no viene, no forzamos hoy en edición)
                if (rec.Invoice_Date__c) {
                    this.invoiceForm.invoiceDate = rec.Invoice_Date__c.substring(0, 10)
                    this.invoiceDateValue = new Date(this.invoiceForm.invoiceDate)
                    this.invoiceDateString = DateTime.fromJSDate(this.invoiceDateValue).toISODate()
                    this.validateInvoiceDate()
                } else {
                    this.invoiceForm.invoiceDate = ''
                }

                // Profesional (combobox) -> respetar carga diferida de opciones
                if (rec.Fabricator__c) {
                    if (!this.loadingCollaborators && this.collaborators?.length) {
                        const found = this.collaborators.find(c => c.value === rec.Fabricator__c)
                        this.invoiceForm.professional = found ? found.value : rec.Fabricator__c
                        this.selectedProfessional = found || null
                    } else {
                        this.pendingProfessionalId = rec.Fabricator__c // se aplicará cuando carguen opciones
                    }
                } else {
                    this.invoiceForm.professional = ''
                }

                // Team member (combobox) -> respetar carga diferida de opciones
                if (rec.ContactId__c) {
                    if (!this.loadingTeamMembers && this.teamMemberOptions?.length) {
                        const found = this.teamMemberOptions.find(tm => (tm.id) === rec.ContactId__c)
                        if (found) {
                            this.invoiceForm.teamMember = (found.id || found.value || '').toString()
                            this.selectedTeamMember = found
                        } else {
                            this.invoiceForm.teamMember = rec.ContactId__c
                        }
                    } else {
                        this.pendingTeamMemberId = rec.ContactId__c // se aplicará al cargar opciones
                    }
                } else {
                    this.invoiceForm.teamMember = ''
                }

                // Adjuntos remotos
                if (urls.length) {
                    const existingNames = new Set(this.uploadedInvoices.map(i => i.name))
                    urls.forEach((url, i) => {
                        const name = this.deriveNameFromUrl(url, i)
                        if (!existingNames.has(name)) {
                            this.uploadedInvoices.push({
                                name,
                                type: this.guessMimeFromUrl(name),
                                size: 0,
                                url,
                                isRemote: true
                            })
                        }
                    })
                }

                // ----- Step 3: warranty -----
                const email = (rec.Warranty_Customer_Email__c || '').trim()
                const detailFields = [
                    rec.Warranty_Customer_Name__c,
                    rec.Warranty_Customer_Last_Name__c,
                    //rec.Warranty_Customer_Country__c,
                    rec.Warranty_Customer_Address__c,
                    rec.Warranty_Customer_City__c,
                    rec.Warranty_Customer_State__c,
                    rec.Warranty_Customer_ZIP_Code__c,
                    rec.Warranty_Customer_Phone__c
                ].map(v => (v || '').trim())

                const hasStep1 = jobItems.length > 0
                // Para decidir el paso, usamos SOLO lo que viene en rec (no lo que ya tengamos en el form)
                const hasStep2 = !!(rec.Fabricator__c || rec.ContactId__c || rec.Invoice_Date__c || urls.length > 0)
                const hasAnyDetail = this.status !== 'Draft' && detailFields.some(Boolean)
                const hasEmailOnly = !!email && !hasAnyDetail
                const hasWarrantyDetails = hasAnyDetail

                if (hasWarrantyDetails) {
                    this.warrantyForm = {
                        ...this.warrantyForm,
                        name: rec.Warranty_Customer_Name__c || '',
                        surname: rec.Warranty_Customer_Last_Name__c || '',
                        country: rec.Warranty_Customer_Country__c || '',
                        zip: rec.Warranty_Customer_ZIP_Code__c || '',
                        state: rec.Warranty_Customer_State__c || '',
                        city: rec.Warranty_Customer_City__c || '',
                        address: rec.Warranty_Customer_Address__c || '',
                        email: email || this.warrantyForm.email,
                        phone: rec.Warranty_Customer_Phone__c || '',
                        gdprConfirmation: false,
                        clientApproval: false
                    }
                } else if (hasEmailOnly) {
                    this.warrantyForm = {
                        ...this.warrantyForm,
                        email,
                        gdprConfirmation: false,
                        clientApproval: false
                    }
                }

                // ----- Decidir paso inicial -----
                if(this.isFabricatorForm && this.status && this.status !== 'Draft'){
                    this.isFabricatorForm = false
                }

                if(this.isDistributorForm && this.status && this.status !== 'Draft'){
                    this.isDistributorForm = false
                }

                if(this.status === 'Denied'){
                    this.step = 1
                } else if (warrantyStep) {
                    this.step = 3
                    this.selectedWarrantyOption = warrantyStep
                    this.warrantyFormMode = warrantyStep
                    this.forceWarrantyForm = warrantyStep == 'activate'
                } else if (hasWarrantyDetails || hasEmailOnly) {
                    this.step = 3
                    this.selectedWarrantyOption = hasWarrantyDetails || this.forceWarrantyForm ? 'activate' : 'instructions'
                    this.warrantyFormMode = this.selectedWarrantyOption
                } else if (hasStep2) {
                    this.step = 2
                    this.selectedWarrantyOption = null
                    this.warrantyFormMode = null
                } else if (hasStep1) {
                    this.step = 1
                    this.selectedWarrantyOption = null
                    this.warrantyFormMode = null
                } else {
                    this.step = 1
                }

                this.setupChildAccounts()

                this.cdr.detectChanges()
            },
            error: (err) => {
                console.error('Error cargando draft sale:', err)
                this.uploadError = 'Error al cargar el borrador. Verifica el backend.'
                this.cdr.detectChanges()
            }
        })
    }

    // ------------------ ACTIONS ------------------
    public openFileInput(): void {
        if (!this.isSelecting) {
            this.isSelecting = true
            this.fileInput.nativeElement.click()
        }
    }

    public onSaveAndContinue() {
        if ((this.step === 1 && this.addingNew) || this.step === 2 || this.step === 3) {
            this.onContinue()
        } else {
            this.saveDraft(true, false, true)
        }
    }

    public onContinue(): void {
        if (!this.canContinue()) return

        if (this.step === 2) {
            this.analytics.trackCustom('new_sale', {
                flow_name: 'new-sale',
                form_option: this.selectedProfessional?.value || 'not-available',
                form_option1: 'not-available',
                form_option2: 'not-available',
                step: 'step2_attach-invoice'
            })

            this.saveDraft(false, true, true)

            if (this.isInvoiceDateOverLimit()) {
                return
            }

        }

        if (this.step === 3) {
            if (!this.warrantyFormMode) {
                if (!this.selectedWarrantyOption) return
                this.warrantyFormMode = this.selectedWarrantyOption

                this.analytics.trackCustom('new_sale', {
                    flow_name: 'new-sale',
                    form_option: this.selectedWarrantyOption,
                    form_option1: 'not-available',
                    form_option2: 'not-available',
                    step: 'step3_how-to-activate-warranty'
                })

                return
            }
            this.finishAndRedirect()
            return
        }

        if (this.step === 1 && this.addingNew) {
            // Push para añadir nueva aplicación
            this.analytics.trackCustom('new_sale', {
                flow_name: 'new-sale',
                form_option: this.selectedApplication?.value || 'not-available',
                form_option1: this.selectedBrand?.value || 'not-available',
                form_option2: this.selectedColor?.value || 'not-available',
                step: 'step0_add-application'
            })
            this.saveOrUpdateApplication()
        } else {
            // Push para confirmar todas las aplicaciones
            this.analytics.trackCustom('new_sale', {
                flow_name: 'new-sale',
                form_option: this.addedApplications.length,
                form_option1: 'not-available',
                form_option2: 'not-available',
                step: 'step1_add-all-applications'
            })
            this.nextStep()
        }
    }

    public goBack(): void {
        if (this.step === 3 && this.warrantyFormMode !== null) {
            this.warrantyFormMode = null
        } else if (this.step === 3 && this.api.userInfo.value?.accountType === 'FABRICATOR') {
            this.isFabricatorForm = true
            this.step = 1
        } else if (this.step === 3 && this.api.userInfo.value?.accountType === 'DISTRIBUTOR') {
            this.isDistributorForm = true
            this.step = 1
        } else if (this.step === 3) {
            this.router.navigate(['/my-sales'])
        } else if (this.step === 1) {
            this.router.navigate(['/my-sales'])
        } else if (this.step > 1) {
            this.step--
        }
    }

    public addNewApplication(): void {
        this.resetForm()
        this.addingNew = true
    }

    public cancelAddOrEdit(): void {
        this.addingNew = false
        this.resetForm()
    }

    public deleteApplication(index: number): void {
        this.addedApplications.splice(index, 1)
        if (this.addedApplications.length === 0) {
            this.resetForm()
            this.addingNew = true
        }
    }

    public editApplication(index: number): void {
        this.editingIndex = index
        const app = this.addedApplications[index]
        this.form = {
            application: app.application,
            units: app.units || '',
            brand: app.brand,
            color: app.color,
            colorCode: app.colorCode
        }
        this.selectedApplication = this.applicationOptions.find((application) => application.id === app.application) || null
        this.selectedUnits = this.unitsOptions.find((units) => units.id === app.units) || null
        this.selectedBrand = this.brandOptions.find((brand) => brand.id === app.brand) || null
        this.selectedColor = this.colorOptions.find((color) => color.id === app.colorCode) || null
        this.addingNew = true
        this.cdr.detectChanges()
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement
        if (input.files && input.files.length > 0) {
            this.handleFiles(input.files)
        } else {
            this.isSelecting = false
        }
        if (this.fileInput) {
            this.fileInput.nativeElement.value = ''
        }
    }

    public onDragOver(event: DragEvent): void {
        event.preventDefault()
        event.stopPropagation()
        this.isDragging = true
    }

    public onDragLeave(): void {
        this.isDragging = false
    }

    public onDrop(event: DragEvent): void {
        event.preventDefault()
        event.stopPropagation()
        this.isDragging = false
        if (event.dataTransfer?.files) {
            this.handleFiles(event.dataTransfer.files)
        }
    }

    public deleteInvoice(index: number): void {
        this.uploadedInvoices.splice(index, 1)
    }

    public previewInvoice(invoice: { name: string; type: string; size: number; file?: File; url?: string }): void {
        if (invoice.url) {
            window.open(invoice.url, '_blank')
            return
        }
        if (invoice.file) {
            const url = URL.createObjectURL(invoice.file)
            window.open(url, '_blank')
        }
    }

    public canContinue(): boolean {
        if (this.step === 3) {
            if (this.warrantyFormMode === null) {
                return this.selectedWarrantyOption !== null
            }
            return this.isFinalStepValid()
        }
        if (this.step === 1 && this.addingNew) {
            const isFormValid =
                this.form.application?.trim() &&
                this.form.brand?.trim() &&
                this.form.color?.trim()
            if (!isFormValid) return false
            const needsUnits = this.showUnits()
            return !needsUnits || (needsUnits && !!this.form.units?.trim())
        } else if (this.step === 2) {
            return this.invoiceForm.professional.trim() !== '' &&
                this.invoiceForm.teamMember.trim() !== '' &&
                this.invoiceForm.invoiceDate.trim() !== '' &&
                !this.invoiceDateError &&
                this.uploadedInvoices.length > 0 &&
                this.acceptTerms
        }
        return true
    }

    // ------------------ PRIVATE HELPERS ------------------
    private saveOrUpdateApplication(): void {
        if (this.editingIndex !== null) {
            this.addedApplications[this.editingIndex] = { ...this.form }
        } else {
            this.addedApplications.push({ ...this.form })
        }
        this.addingNew = false
        this.resetForm()
    }

    private nextStep(): void {
        if (this.step < this.progressSteps.length) {
            this.step++
        }
    }

    private resetForm(): void {
        this.editingIndex = null
        this.form = { application: '', units: '', brand: '', color: '', colorCode: '' }
        this.uploadError = null
        this.resetMaterialSelection()
    }


    private submitForm(): void {
        console.log('Enviando formulario:', {
            applications: this.addedApplications,
            invoices: this.uploadedInvoices.map(invoice => ({
                name: invoice.name,
                type: invoice.type,
                size: invoice.size
            })),
            invoiceDetails: this.invoiceForm
        })
    }

    private getTodayDate(): string {
        const today = new Date()
        return today.toISOString().split('T')[0]
    }

    public validateInvoiceDate(): void {
        const selectedDate = new Date(this.invoiceForm.invoiceDate)
        const today = new Date(this.getTodayDate())
        this.invoiceDateError = selectedDate > today
    }

    public showUnits(): boolean {
        return this.applicationsWithUnits.includes(this.form.application)
    }

    public isImage(fileType: string): boolean {
        return fileType === 'image/png' || fileType === 'image/jpeg'
    }

    public selectApplication(value: SelectValue | null): void {
        this.form.application = value ? value.value : ''
        this.form.units = ''
        this.selectedApplication = value
        this.selectedUnits = null
    }

    public selectUnits(value: SelectValue | null): void {
        this.form.units = value ? value.value : ''
        this.selectedUnits = value
    }

    public selectBrands(value: SelectValue | null): void {
        this.form.brand = value ? value.value : ''
        this.selectedBrand = value
        this.colorOptions = this.getColorOptions()
        this.form.color = ''
        this.selectedColor = null
        this.cdr.detectChanges()
    }

    public selectColor(value: SelectValue | null): void {
        this.form.color = value ? value.label : ''
        this.form.colorCode = value ? value.value : ''
        this.selectedColor = value
        if (value) {
            this.form.brand = this.colors.find(c => c.colorCode === value.value)?.brand || this.form.brand
            this.selectedBrand = this.brandOptions.find(b => b.id === this.form.brand?.toLowerCase()) || null
        }
    }

    resetMaterialSelection(){
        this.selectedApplication = null
        this.selectedUnits = null
        this.selectedBrand = null
        this.selectedColor = null
    }

    public selectProfessional(value: SelectValue | null): void {
        this.invoiceForm.professional = value ? value.value : ''
        this.selectedProfessional = value
    }

    public selectTeamMember(value: SelectValue | null): void {
        this.invoiceForm.teamMember = value ? value.value : ''
        this.selectedTeamMember = value
    }

    // public getApplicationOptions(): Option[] {
    //   return [
    //     // { value: 'none', label: 'Ninguno' },
    //      { value: 'indoor_kitchen', label: 'Encimera de cocina' },
    //     // { value: 'outdoor_kitchen', label: 'Cocina Exterior' },
    //     // { value: 'bath', label: 'Baño' },
    //     // { value: 'facade', label: 'Fachada' },
    //     { value: 'flooring', label: 'Solería' },
    //     // { value: 'backsplash', label: 'Salpicadero' },
    //     // { value: 'cladding', label: 'Revestimiento' },
    //     { value: 'interior_cladding', label: 'Revestimiento interior' },
    //     { value: 'furniture_tops_covers', label: 'Cubierta para muebles' },
    //     // { value: 'indoor_countertops', label: 'Encimeras Interiores' },
    //     // { value: 'outdoor_countertops', label: 'Encimeras Exteriores' },
    //     { value: 'sink', label: 'Fregadero' },
    //     { value: 'washbasin', label: 'Lavabo' },
    //     { value: 'shower_tray', label: 'Plato de ducha' },
    //     { value: 'bath_countertop', label: 'Encimera de baño' }
    //   ];
    // }

    public getApplicationOptions(): SelectValue[] {
        const values = [
            'Indoor Kitchen',
            'Sink',
            'Flooring',
            'Interior cladding',
            'Washbasin',
            'Shower Tray',
            'Bath Countertop',
            'Furniture tops/ Covers'
            // 'Outdoor Kitchen',
            // 'Bath',
            // 'Façade',
            // 'Backsplash',
            // 'Cladding',
            // 'Indoor Countertops',
            // 'Outdoor Countertops',
        ]

        // Complete list of possible values in SF
        this.translate.get([
            'my_sales.applications.Indoor Kitchen',
            'my_sales.applications.Outdoor Kitchen',
            'my_sales.applications.Bath',
            'my_sales.applications.Façade',
            'my_sales.applications.Flooring',
            'my_sales.applications.Backsplash',
            'my_sales.applications.Cladding',
            'my_sales.applications.Interior cladding',
            'my_sales.applications.Furniture tops/ Covers',
            'my_sales.applications.Indoor Countertops',
            'my_sales.applications.Outdoor Countertops',
            'my_sales.applications.Sink',
            'my_sales.applications.Washbasin',
            'my_sales.applications.Shower Tray',
            'my_sales.applications.Bath Countertop'
        ])
        this.translate.get(values)

        return values.map(v => ({
            id: v,
            value: v,
            label: this.getApplicationLabel(v)
        }))
    }

    public getBrandsOptions(): SelectValue[] {
        return this.brands
            .filter(brand => brand.toLowerCase() !== 'prexury')
            .map(brand => ({
                id: brand.toLowerCase(),
                value: brand.toLowerCase(),
                label: brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase()
            }))
    }

    public getColorOptions(): SelectValue[] {
        const filteredColors = this.form.brand
            ? this.colors.filter(color => color.brand?.toLowerCase() === this.selectedBrand?.value)
            : this.colors

        const uniqueColors = new Map<string, SelectValue>()

        filteredColors.forEach(color => {
            const key = `${color.colorCode}-${color.colorName}`.toLowerCase()
            if (!uniqueColors.has(key)) {
                uniqueColors.set(key, {
                    id: color.colorId || color.colorCode || color.colorName || '',
                    value: color.colorId || color.colorCode || color.colorName || '',
                    label: color.colorName || ''
                })
            }
        })

        return Array.from(uniqueColors.values()).sort((a, b) => {
            const labelA = (a.label || '').toLowerCase()
            const labelB = (b.label || '').toLowerCase()
            return labelA.localeCompare(labelB, undefined, { sensitivity: 'base' })
        })
    }

    public getUnitsOptions(): SelectValue[] {
        return Array.from({ length: 10 }, (_, i) => ({
            id: (i + 1).toString(),
            value: (i + 1).toString(),
            label: (i + 1).toString()
        }))
    }

    private transformCollaborators(collaborators: any[]): SelectValue[] {
        return collaborators.map(collaborator => {
            let value = collaborator.id || collaborator.value || collaborator.toString()
            let label = collaborator.name || collaborator.label || collaborator.toString() || value

            if (typeof collaborator === 'object' && collaborator !== null) {
                value = collaborator.id || collaborator.value || Object.keys(collaborator)[0] || value
                label = collaborator.name || collaborator.label || collaborator[value] || label
            }

            return { id: value.toString(), value: value.toString(), label: label.toString() }
        }).filter(option => option.value && option.label)
    }

    public getProfessionalOptions(): SelectValue[] {
        return this.loadingCollaborators ? [{
            id: 'loading',
            value: 'loading',
            label: 'Cargando...'
        }] : this.collaborators
    }

    public getTeamMemberOptions(): SelectValue[] {
        return this.loadingTeamMembers ? [{
            id: 'loading',
            value: 'loading',
            label: 'Cargando...'
        }] : this.transformCollaborators(this.teamMembers)
    }

    public get minDate(): string {
        return this.getTodayDate()
    }

    onOpenFileSelect() {
        this.fileInput.nativeElement.click()
    }

    get initialDateValue(): Date | null {
        return this.invoiceForm.invoiceDate ? new Date(this.invoiceForm.invoiceDate) : null
    }

    private formatDate(date: Date): string {
        const day = date.getDate().toString().padStart(2, '0')
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const year = date.getFullYear()
        return `${day}/${month}/${year}`
    }

    onDateChanged(value: Date | null) {
        if (value) {
            this.invoiceForm.invoiceDate = value.toISOString().split('T')[0]
            this.validateInvoiceDate()
        } else {
            this.invoiceForm.invoiceDate = ''
            this.invoiceDateError = false
        }
    }

    onDateCleared() {
        this.invoiceForm.invoiceDate = ''
        this.invoiceDateError = false
    }

    private formatDateMMDDYYYY(date: Date): string {
        const month = date.getMonth() + 1
        const day = date.getDate()
        const year = date.getFullYear()
        return `${month}/${day}/${year}`
    }

    onCountryChange(selected: SelectValue | null) {
        if (selected) {
            this.warrantyForm.country = selected.value
        } else {
            this.warrantyForm.country = ''
        }
        this.selectedCountry = selected
    }

    onWarrantyPostalSelected(suggestion: { postalCode: string; city: string; country: string }) {
        this.warrantyForm.zip = suggestion.postalCode
        if (suggestion.city) {
            this.warrantyForm.city = suggestion.city
        }
    }

    private isFinalStepValid(): boolean {
        if (this.warrantyFormMode === null) return false

        const f = this.warrantyForm
        if (this.warrantyFormMode === 'activate') {
            return !!(
                f.name?.trim() &&
                f.surname?.trim() &&
                f.country?.trim() &&
                f.zip?.trim() &&
                f.state?.trim() &&
                f.city?.trim() &&
                f.address?.trim() &&
                f.email?.trim() &&
                this.isValidEmail(f.email) &&
                f.phone?.trim() &&
                f.gdprConfirmation === true &&
                f.clientApproval === true
            )
        }

        if (this.warrantyFormMode === 'instructions') {
            return (this.warrantyForm.instructionOption === 'mail' && this.isWarrantyMailInfoValid()) ||
                this.warrantyForm.instructionOption === 'pdf'
        }

        return false
    }

    isWarrantyMailInfoValid() {
        if (!this.warrantyForm)
            return false

        return !!(
            this.warrantyForm.email?.trim() &&
            this.isValidEmail(this.warrantyForm.email) &&
            this.warrantyForm.gdprConfirmation === true &&
            this.warrantyForm.clientApproval === true
        )
    }

    private buildDraftBody(status: 'Draft' | 'Requested'): SaveSalesDraftBody & UpdateSaleBody {
        const invoiceDate = this.invoiceDateValue ? DateTime.fromJSDate(this.invoiceDateValue).toISODate() : null
        let statusToSave: 'Draft' | 'Requested' | undefined = status;
        if(statusToSave === 'Draft' && this.status === 'Denied') {
            statusToSave = undefined;
        }

        return {
            fabricatorName: this.api.userInfo.value?.accountId || '',
            contactId: this.api.userInfo.value?.id || '',
            invoiceDate: invoiceDate || this.getTodayDate(),
            invoiceNumber: this.invoiceIdFab || undefined,
            teamMember: this.invoiceForm.teamMember || '',
            saleItems: this.addedApplications.map(app => {
                const selectedColor = this.colors.find(
                    c => c.colorCode === app.colorCode || c.colorId === app.color || c.colorName === app.color
                )
                return {
                    id: app.id,
                    applicationTypeLabel: app.application,
                    brand: selectedColor?.brand || app.brand,
                    productCode: selectedColor?.colorCode || app.colorCode || app.color,
                    quantity: Number(app.units || 1),
                    sku: app.sku
                }
            }),

            attachmentUrlsToKeep: this.uploadedInvoices
                .filter(i => i.isRemote && i.url)
                .map(i => i.url as string),

            warrantyCustomerAddress: this.warrantyForm.address || '',
            warrantyCustomerCity: this.warrantyForm.city || '',
            warrantyCustomerCountry: this.warrantyForm.country || '',
            warrantyCustomerEmail: this.warrantyForm.email || '',
            warrantyCustomerLastName: this.warrantyForm.surname || '',
            warrantyCustomerName: this.warrantyForm.name || '',
            warrantyCustomerPhone: this.warrantyForm.phone || '',
            warrantyCustomerState: this.warrantyForm.state || '',
            warrantyCustomerZIPCode: this.warrantyForm.zip || '',
            attachments: this.uploadedInvoices.filter(i => !!i.file).map(i => i.file as File),
            status__c: statusToSave,

            fabricator__c: this.invoiceForm.professional || '',
            kbStudioId: this.kbStudio || undefined
        }
    }

    public async saveDraft(shouldContinue: boolean, isFinal = false, isOnContinuePressed = false): Promise<void> {
        if (!this.api.userInfo.value?.accountId || !this.api.userInfo.value?.id) await Promise.resolve()

        const body = this.buildDraftBody(isFinal ? 'Requested' : 'Draft')

        if(isFinal && this.invoiceDateValue){
            this.invoiceDateString = DateTime.fromJSDate(this.invoiceDateValue).toISODate()
        }

        if (this.saleId) {
            // EDITAR existente → PUT /sales/{id}

            return await new Promise<void>((resolve, reject) => {
                console.log('Actualizando borrador existente con ID:', this.saleId, 'y body:', body)
                this.api.updateSale(this.saleId as string, body).subscribe({
                    next: (resp) => {
                        console.log('Borrador actualizado:', resp)
                        if (resp && resp.points) {
                            this.salePoints = resp.points
                        }

                        if (this.step === 1) {
                            this.analytics.trackCustom('click', {
                                click_type: 'button',
                                click_action: 'save-draft',
                                type: 'step1_new-sale'
                            })
                        }

                        if (this.step === 2 && this.isInvoiceDateOverLimit()) {
                            this.navigateToMySales()
                        }

                        if (shouldContinue) {
                            this.onContinue()
                        }
                        resolve()
                    },
                    error: (err) => {
                        console.error('Error al actualizar borrador:', err)
                        reject(err)
                    }
                })
            })
        } else {
            // CREAR nuevo → POST /sales/draft
            if ((this.step === this.progressSteps.length && this.warrantyFormMode !== null && isOnContinuePressed)
                || !isOnContinuePressed
                || isFinal) {

                return await new Promise<void>((resolve, reject) => {
                    this.api.saveSalesDraft(body).subscribe({
                        next: (resp) => {
                            console.log('Borrador creado:', resp)

                            if (!this.saleId) {
                                this.saleId = resp.id
                                this.location.replaceState(`/new-sales/${this.saleId}`)
                            }

                            if (this.step === 1) {
                                this.analytics.trackCustom('click', {
                                    click_type: 'button',
                                    click_action: 'save-draft',
                                    type: 'step1_new-sale'
                                })
                            }

                            // Si es final, registrar la venta (status Requested) tras crear el borrador
                            if (isFinal && this.saleId) {
                                const bodyRequested = this.buildDraftBody('Requested')
                                this.api.updateSale(this.saleId, bodyRequested).subscribe({
                                    next: (updateResp) => {
                                        console.log('Venta registrada:', updateResp)
                                        if (updateResp && updateResp.points) {
                                            this.salePoints = updateResp.points
                                        }
                                        if (this.step === 2 && this.isInvoiceDateOverLimit()) {
                                            this.navigateToMySales()
                                            resolve()
                                            return
                                        }
                                        if (shouldContinue) {
                                            this.onContinue()
                                        }
                                        resolve()
                                    },
                                    error: (updateErr) => {
                                        if (updateErr?.status === 200) {
                                            if (this.step === 2 && this.isInvoiceDateOverLimit()) {
                                                console.error('Error al registrar la venta:', updateErr)
                                                reject(updateErr)
                                                return
                                            }
                                            if (shouldContinue) this.onContinue()
                                            resolve()
                                            return
                                        }
                                        console.error('Error al registrar la venta:', updateErr)
                                        reject(updateErr)
                                    }
                                })
                                return
                            }

                            if (shouldContinue) {
                                this.onContinue()
                            }
                            resolve()
                        },
                        error: (err) => {
                            console.error('Error al crear borrador:', err)
                            reject(err)
                        }
                    })
                })
            } else {
                if (shouldContinue) {
                    this.onContinue()
                }
            }
        }
    }


    private finishAndRedirect(): void {
        if (!this.api.userInfo.value?.accountId || !this.api.userInfo.value?.id) {
            console.error('Error: userInfo no está disponible o no contiene accountId/id')
            return
        }


        const showModal = () => {
            this.showFinishModal = true

            try {
                window.scrollTo({ top: 0 })
            } catch {
            }
            this.cdr.detectChanges()
        }

        if (this.saleId) {

            const bodyRequested = this.buildDraftBody('Requested')

            if (this.warrantyFormMode === 'activate') {
                bodyRequested.registerWarranty = true

                this.analytics.trackCustom('new_sale', {
                    flow_name: 'new-sale',
                    form_option: 'activate-warranty',
                    form_option1: 'not-available',
                    form_option2: 'not-available',
                    step: 'step4_activate-warranty'
                })
            }
            if (this.warrantyFormMode === 'instructions' && this.warrantyForm && this.warrantyForm.instructionOption === 'mail') {
                bodyRequested.sendWarrantyInstructions = true

                this.analytics.trackCustom('new_sale', {
                    flow_name: 'new-sale',
                    form_option: 'send-instructions',
                    form_option1: 'send-email',
                    form_option2: 'not-available',
                    step: 'step4_activate-warranty'
                })
            }
            if (this.warrantyFormMode === 'instructions' && this.warrantyForm && this.warrantyForm.instructionOption === 'pdf') {
                this.analytics.trackCustom('new_sale', {
                    flow_name: 'new-sale',
                    form_option: 'send-instructions',
                    form_option1: 'download-pdf',
                    form_option2: 'not-available',
                    step: 'step4_activate-warranty'
                })

                this.api.downloadWarrantyInstructions(this.saleId).subscribe({
                    next: (blob) => {
                        console.log(blob)
                        if (blob !== null && blob.size > 0) {
                            const blobUrl = window.URL.createObjectURL(blob)

                            const a = document.createElement('a')
                            a.href = blobUrl
                            a.download = this.saleId + '.pdf'
                            a.click()

                            // Limpieza
                            window.URL.revokeObjectURL(blobUrl)
                            this.navigateToMySales()
                        }
                    }
                })
                return
            }

            this.api.updateSale(this.saleId, bodyRequested).subscribe({
                next: () => showModal(),
                error: (error) => {
                    if (error?.status === 200) {
                        showModal()
                        return
                    }
                    console.error('Error al actualizar (finish):', error)
                }
            })
            return
        }

        // No existe → crear como Draft y luego subir a Requested
        const bodyDraft = this.buildDraftBody('Draft')
        this.api.saveSalesDraft(bodyDraft).subscribe({
            next: (resp: any) => {
                const newId = resp?.id || resp?.Id
                if (!newId) {
                    console.warn('No se obtuvo ID tras crear draft')
                    showModal()
                    return
                }

                const bodyRequested = this.buildDraftBody('Requested')
                this.api.updateSale(newId, bodyRequested).subscribe({
                    next: () => showModal(),
                    error: (e2) => {
                        if (e2?.status === 200) {
                            showModal()
                            return
                        }
                        console.error('Error al elevar a Requested:', e2)
                    }
                })
            },
            error: (error) => {
                if (error?.status === 200) {
                    showModal()
                    return
                }
                console.error('Error al crear draft:', error)
            }
        })
    }


    private deriveNameFromUrl(url: string, idx: number): string {
        try {
            const last = url.split('?')[0].split('/').pop() || `invoice_${idx + 1}`
            return decodeURIComponent(last)
        } catch {
            return `invoice_${idx + 1}`
        }
    }

    private guessMimeFromUrl(name: string): string {
        const ext = name.split('.').pop()?.toLowerCase()
        if (ext === 'png') return 'image/png'
        if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg'
        if (ext === 'pdf') return 'application/pdf'
        return 'application/octet-stream'
    }


    /** Muestra 1.2 KB, 3.4 MB, etc. */
    public formatSize(bytes: number): string {
        if (!bytes && bytes !== 0) return ''
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        const value = bytes / Math.pow(k, i)
        return `${value.toFixed(value >= 100 || i === 0 ? 0 : 1)} ${sizes[i]}`
    }

    /** Descarga el adjunto, sea File local o URL remota */
    public async downloadInvoice(invoice: {
        name: string;
        type: string;
        size: number;
        file?: File;
        url?: string;
        isRemote?: boolean
    }): Promise<void> {
        try {
            // Caso 1: archivo local (subido desde <input type="file">)
            if (invoice.file) {
                const blobUrl = URL.createObjectURL(invoice.file)
                this.triggerDownload(blobUrl, invoice.name)
                // Revocamos tras un tick para no romper la descarga
                setTimeout(() => URL.revokeObjectURL(blobUrl), 0)
                return
            }

            // Caso 2: URL remota
            if (invoice.url) {
                // Intento 2.1: descargar como blob para respetar el nombre del archivo (puede fallar por CORS)
                try {
                    const res = await fetch(invoice.url, { mode: 'cors' })
                    if (res.ok) {
                        const blob = await res.blob()
                        const blobUrl = URL.createObjectURL(blob)
                        // Si el nombre no tiene extensión, deducimos por MIME
                        const filename = this.ensureFilenameHasExtension(invoice.name, invoice.type)
                        this.triggerDownload(blobUrl, filename)
                        setTimeout(() => URL.revokeObjectURL(blobUrl), 0)
                        return
                    }
                    // Si el fetch no es ok, caemos al fallback
                } catch {
                    // Fallback 2.2: abrir en nueva pestaña (el navegador decidirá descargar o mostrar)
                    window.open(invoice.url, '_blank')
                    return
                }

                // Fallback por si res.ok fue false
                window.open(invoice.url, '_blank')
                return
            }

            console.warn('No hay file ni url para descargar.')
        } catch (e) {
            console.error('Error al descargar el archivo:', e)
        }
    }

    private triggerDownload(href: string, filename: string): void {
        const a = document.createElement('a')
        a.href = href
        a.download = filename || 'archivo'
        a.rel = 'noopener'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
    }

    private ensureFilenameHasExtension(name: string, mime?: string): string {
        const hasExt = /\.[a-z0-9]+$/i.test(name || '')
        if (hasExt || !mime) return name || 'archivo'
        const ext = this.getExtensionFromMime(mime)
        return (name || 'archivo') + (ext ? `.${ext}` : '')
    }

    private getExtensionFromMime(mime: string): string | '' {
        const m = (mime || '').toLowerCase()
        if (m === 'image/png') return 'png'
        if (m === 'image/jpeg') return 'jpg'
        if (m === 'application/pdf') return 'pdf'
        return ''
    }


    public get uploadDisabled(): boolean {
        return this.uploadedInvoices.length >= 1
    }

    private handleFiles(files: FileList): void {
        this.uploadError = null

        if (this.uploadDisabled) {
            this.uploadError = 'my_sales.file_limit_reached'
            this.isSelecting = false
            if (this.fileInput) this.fileInput.nativeElement.value = ''
            return
        }

        const first = files.item(0)
        if (!first) {
            this.isSelecting = false
            return
        }

        if (!this.ALLOWED_FILE_TYPES.includes(first.type)) {
            this.uploadError = 'my_sales.invalid_file_type'
        } else if (first.size > this.MAX_FILE_SIZE) {
            this.uploadError = 'my_sales.file_too_large'
        } else {
            this.uploadedInvoices.push({
                name: first.name,
                type: first.type,
                size: first.size,
                file: first
            })
        }

        if (this.fileInput) this.fileInput.nativeElement.value = ''
        this.isSelecting = false
    }


    public getColorThumb(app: { brand: string; color: string; colorCode: string; }): string {
        const code = this.resolveColorCode(app.colorCode || app.color, app.brand)
        return this.buildColorThumbUrl(code)
    }

    private resolveColorCode(value: string, brand?: string): string {
        const v = (value || '').toString()
        if (!v) return ''

        let item = this.colors.find(c => (c as any).colorId?.toString() === v)

        if (!item) {
            item = this.colors.find(
                c =>
                    c.colorName?.toLowerCase() === v.toLowerCase() &&
                    (!brand || c.brand === brand)
            )
        }

        if (!item) {
            item = this.colors.find(c => c.colorCode?.toLowerCase() === v.toLowerCase())
        }

        return item?.colorCode || v
    }

    private buildColorThumbUrl(code: string): string {
        if (!code) return 'assets/images/sample-timber.jpg'
        const safe = encodeURIComponent(code)
        return `https://assetstools.cosentino.com/api/v1/bynder/color/${safe}/detalle/${safe}-thumb.jpg?w=200&h=100&auto=compress%2Cformat`
    }


    public getApplicationLabel(value: string): string {

        const key = `my_sales.applications.${value}`
        const translated = this.translate.instant(key)
        return translated && translated !== key ? translated : value
    }

    private isValidEmail(email: string): boolean {

        const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/ //NOSONAR
        return re.test((email || '').trim())
    }


    public closeFinishModal(): void {
        this.showFinishModal = false
    }

    public navigateToMySales(): void {
        if (this.isConfirmActivateWarrantyModalOpen) {
            this.router.navigate(['/my-sales'])
        } else if (this.step === 3 && !this.warrantyFormMode) {
            this.isConfirmActivateWarrantyModalOpen = true
        } else {
            this.router.navigate(['/my-sales'])
        }
    }


    resetClientInstructionsWarrantyCheckboxes(value: any) {
        this.warrantyForm.email = ''
        this.warrantyForm.gdprConfirmation = false
        this.warrantyForm.clientApproval = false
    }

    isInvoiceDateOverLimit() {
        if (!this.invoiceDateValue)
            return false
        const today = DateTime.now()
        const invoiceDate = DateTime.fromJSDate(this.invoiceDateValue)
        return today.diff(invoiceDate, 'days').days > this.ACTIOS_CUT_OFF_DAYS
    }

    onShopSelected($event: SelectValue | null) {
        this.selectedShop = $event
        this.canContinueToSalesCreation = !!this.selectedShop;
        if(this.selectedShop){
            this.kbStudio = <string>this.selectedShop.id
        }
    }

    onShopConfirmed() {
        this.needToSelectShop = false
        this.loadCollaborators()
        this.loadTeamMembers()
    }

    onSaveFabricatorForm(invoicesSelected: InvoiceData[]){
        this.addedApplications = []
        let lowestDate: DateTime<true> | DateTime<false> = DateTime.now();
        let invoices: string[] = []
        for(let invoice of invoicesSelected){
            if(invoice.invoiceId){
                invoices.push(invoice.invoiceId)
            }
            this.addedApplications.push({
                application: '*None*',
                brand: invoice.brand || '',
                color: invoice.colorName || '',
                colorCode: invoice.colorCode || '',
                units: '1',
                sku: invoice.materialId || ''
            })
            if(!lowestDate && invoice.createdDate){
                lowestDate = DateTime.fromISO(invoice.createdDate)
            } else if(invoice.createdDate && lowestDate && DateTime.fromISO(invoice.createdDate).diff(lowestDate, 'days').days < 0){
                lowestDate = DateTime.fromISO(invoice.createdDate)
            }
        }
        lowestDate = DateTime.now()
        this.invoiceDateValue = lowestDate?.toJSDate()
        this.invoiceDateString = lowestDate?.toISODate()
        this.invoiceIdFab = invoices.join(',')

        this.saveDraft(false, true).then(() => {
            console.log('After saveDraft finished')
            if(this.isInvoiceDateOverLimit()){
                this.navigateToMySales()
            } else {
                this.step = 3
                this.isFabricatorForm = false
                this.isDistributorForm = false
            }
        }).catch( err => {
            console.error('Error saving draft:', err)
        })
    }
}
